import React, { useEffect, useState } from "react";
import { getStats, listActions } from "@/lib/api";
import StatusPill from "@/components/StatusPill";
import DetailDrawer from "@/components/DetailDrawer";

function StatCard({ label, value, accent, testid, delay = 0 }) {
    return (
        <div
            className="border border-[#262a2e] bg-[#111315] p-6 pc-fade-up"
            style={{ animationDelay: `${delay}ms` }}
            data-testid={testid}
        >
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af]">
                {label}
            </div>
            <div
                className="font-mono text-5xl mt-3 tracking-tight"
                style={{ color: accent || "#f3f4f6" }}
            >
                {value ?? "—"}
            </div>
        </div>
    );
}

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [feed, setFeed] = useState([]);
    const [selected, setSelected] = useState(null);
    const [open, setOpen] = useState(false);

    const load = async () => {
        const [s, f] = await Promise.all([getStats(), listActions()]);
        setStats(s);
        setFeed(f.slice(0, 15));
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, []);

    return (
        <div data-testid="dashboard-page">
            <div className="mb-8">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7cc93c] mb-2">
                    // command surface
                </div>
                <h1 className="text-4xl font-bold tracking-tight">Dashboard</h1>
                <p className="font-mono text-[11px] text-[#9ca3af] mt-1">
                    Immediate visibility into every automated action. No silent failures.
                </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
                <StatCard
                    label="Total today"
                    value={stats?.today?.total}
                    testid="stat-total"
                    delay={0}
                />
                <StatCard
                    label="Success"
                    value={stats?.today?.success}
                    accent="#7cc93c"
                    testid="stat-success"
                    delay={60}
                />
                <StatCard
                    label="Failed"
                    value={stats?.today?.failed}
                    accent="#ff453a"
                    testid="stat-failed"
                    delay={120}
                />
                <StatCard
                    label="Pending"
                    value={stats?.today?.pending}
                    accent="#ff9f0a"
                    testid="stat-pending"
                    delay={180}
                />
                <StatCard
                    label="Needs review"
                    value={stats?.today?.needs_review}
                    accent="#ff9f0a"
                    testid="stat-review"
                    delay={240}
                />
            </div>

            <div className="border border-[#262a2e] bg-[#111315]">
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#262a2e]">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af]">
                        live activity feed
                    </div>
                    <span className="font-mono text-[10px] text-[#9ca3af] flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#7cc93c] pc-dot-pulse" />
                        streaming · polls 5s
                    </span>
                </div>
                <div data-testid="activity-feed">
                    {feed.length === 0 && (
                        <div className="p-10 text-center font-mono text-[11px] text-[#9ca3af]">
                            NO_DATA_FOUND
                        </div>
                    )}
                    {feed.map((a) => (
                        <div
                            key={a.id}
                            className="pc-row flex items-center gap-4 px-5 py-3 border-b last:border-b-0 border-[#262a2e] cursor-pointer"
                            data-testid={`feed-row-${a.id}`}
                            onClick={() => {
                                setSelected(a);
                                setOpen(true);
                            }}
                        >
                            <div className="font-mono text-[10px] text-[#9ca3af] w-24 shrink-0">
                                {a.created_at?.slice(11, 19)}Z
                            </div>
                            <div className="font-mono text-[11px] w-40 shrink-0 text-[#9ca3af]">
                                {a.type}
                            </div>
                            <div className="font-mono text-[12px] flex-1 truncate">
                                {a.target || "—"}
                            </div>
                            {a.provider && (
                                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#9ca3af] border border-[#262a2e] px-1.5 py-0.5 rounded-[2px]">
                                    {a.provider}
                                </span>
                            )}
                            <StatusPill status={a.status} />
                        </div>
                    ))}
                </div>
            </div>

            <DetailDrawer action={selected} open={open} onOpenChange={setOpen} />
        </div>
    );
}
